import { objectReplace } from '@scalar-org/helpers/objects'
import { safeJSONParse } from '@/helpers/safe-parse'
import { shallowMerge } from '@/helpers/shallow-merge'
import { type ScalarElementTag, scalarElementTags } from '@/elements'
import type { HastElementNode, HastNode } from '@/types'
import type { HeadingRecord } from '@scalar-org/entities/publish'
import { EditorClasses } from '@scalar-org/guide'
import { markdownHeadingDirective } from '@scalar-org/guide/editor/extensions/RichTextExtensions/CustomHeading'

import { markdownPageTitle } from '@scalar-org/guide/editor/extensions/RichTextExtensions/CustomPageTitle'
import { deepClone } from '@scalar-org/helpers/clone'
import { slugify, slugifyJoin } from '@scalar-org/helpers/url'
import { nanoid } from 'nanoid'
import type { Plugin } from 'unified'
import { visit } from 'unist-util-visit'
import { scalarCodeBlock } from '@scalar-org/guide-elements/elements/code-block'

// ---------------------------------------------------------------------------
// During markdown rendering we convert to a HAST tree before doing most of the
// content processing. This allows processing of embedded HTML alongside the parsed
// and transformed markdown nodes.

// Directives that have been processed in the markdown plugin will exist during
// this pipeline step and must be handled carefully to avoid conflicts with users customs tags
// ---------------------------------------------------------------------------

/** Map common markdown language shortcuts to lowlight languages */
const languageMappings: Record<string, string> = {
  'ts': 'typescript',
  'js': 'javascript',
  'py': 'python',
  'c#': 'csharp',
  'c++': 'cpp',
}

/**
 * Managed embedded HTML in markdown. Maps some tags to custom elements
 * and extends the header definitions to include h tags
 *
 * @see `@/markdown/index.ts` for the matching function that handles markdown
 * directive transforms.
 */
export const customHtmlInMarkdownPlugin: (options: {
  pageTitle?: string
  pageDescription?: string
}) => Plugin =
  (
    { pageTitle, pageDescription }, // Plugin generator
  ) =>
  () => {
    /**
     * Plugin function
     *
     * At the root tree we can search for top level document elements such as headings
     *
     * The `visit` call will traverse the AST tree and allow modification of nested nodes
     */
    return (tree) => {
      // Typecast because unified Node types are not playing nice
      const rootChildren = (tree as any).children as unknown as HastNode[]

      /**
       * The first page heading needs to handled explicitly
       * If an h1 tag is provided we just use that.
       * Matching logic in the server will prioritize the first h1 tag for page name
       * If there is not a first heading we will add one with the page name
       */
      const firstPageHeading = rootChildren.find(
        (n) => n.type === 'element' && n.tagName === 'h1',
      ) as HastElementNode | undefined

      if (firstPageHeading) {
        firstPageHeading.properties = firstPageHeading.properties ?? {}
        firstPageHeading.properties.description = pageDescription
      } else if (pageTitle) {
        rootChildren.unshift({
          type: 'element',
          tagName: 'h1',
          children: [
            {
              type: 'text',
              value: pageTitle,
            },
          ],
          properties: {
            description: pageDescription,
          },
        } satisfies HastElementNode)
      }

      // Heading search to generate proper id tags for l2 and l3 headings
      let parentSlug = ''
      rootChildren.forEach((node) => {
        if (node.type === 'element' && /^h[2-3]$/.test(node.tagName)) {
          node.properties = node.properties || {}

          // For l2 headings they are always the root name
          if (node.tagName === 'h2') {
            const slug = slugify(flatContent(node))
            node.properties.slug = slug
            parentSlug = slug
          }

          // For l3 headings we prefix with the preceding l2 heading slug if available
          if (node.tagName === 'h3') {
            node.properties.slug = parentSlug
              ? slugifyJoin(slugify(flatContent(node)), parentSlug)
              : slugify(flatContent(node))
          }
        }

        // Remark raw nests custom HTML elements in a paragraph tag.
        // When we see a single nested element we hoist
        if (
          node.type === 'element' &&
          node.tagName === 'p' &&
          node.children.length === 1 &&
          node.children[0].type === 'element' &&
          node.children[0].tagName.startsWith('scalar')
        ) {
          objectReplace(node, node.children[0])
        }
      })

      visit(tree, (_node: unknown, index, parent: { children: HastNode[] }) => {
        const node = _node as unknown as HastNode

        if (node.type !== 'element') return
        // ---------------------------------------------------------------------------
        // HEADINGS

        if (node.tagName === 'h1') {
          processAttributes({
            node,
            initialAttrs: {
              title: flatContent(node),
              description: node.properties?.description || '',
            },
            tagNameOverride: markdownPageTitle.tagName,
          })
        }

        if (/^h[2-6]$/.test(node.tagName)) {
          processAttributes({
            node,
            initialAttrs: {
              slug: node.properties?.slug || '',
              level: String(Number.parseInt(node.tagName[1]) - 1), // Offset required to match our editor offset
            },
            tagNameOverride: markdownHeadingDirective.tagName,
          })
        }

        // ---------------------------------------------------------------------------
        // HTML CODE BLOCKS
        if (
          ['code', 'scalar-code'].includes(node.tagName) &&
          node.properties?.inline !== 'true'
        ) {
          // For HTML code blocks we skip the first space to allow the tag to sit above any content
          const firstChild = node.children[0]
          if (
            firstChild &&
            firstChild.type === 'text' &&
            firstChild.value.startsWith('\n')
          ) {
            firstChild.value = firstChild.value.slice(1)
          }

          processAttributes({
            node,
            initialAttrs: {
              // If there is mapped language use that otherwise use the given value.
              lang:
                languageMappings[node.properties?.dataLang || ''] ||
                node.properties?.dataLang ||
                'plaintext',
              title: node.properties?.dataTitle || '',
            },
            tagNameOverride: scalarCodeBlock.tagName,
          })
        }

        // ---------------------------------------------------------------------------
        // TABLE

        if (node.tagName === 'table' && index !== undefined) {
          const wrapper: HastElementNode = {
            tagName: 'figure',
            type: 'element',
            children: [node],
            properties: {
              class: [
                EditorClasses.Table,
                EditorClasses.Paragraph,
                'table-wrapper',
                node.properties?.class || '',
              ].join(' '),
            },
          }

          parent.children.splice(index, 1, wrapper)
        }

        // ---------------------------------------------------------------------------
        // CUSTOM ELEMENTS

        /**
         * We only want to process custom element tags that were not previously parsed
         * into those custom tags from a directive
         *
         * The directive parsing managed the prop validation separately and we do not want
         * to duplicate that validation here.
         */
        if (
          scalarElementTags.includes(node.tagName as ScalarElementTag) &&
          !node.properties?.dataScalarDirective
        ) {
          processAttributes({
            node,
            initialAttrs: {},
          })
        }

        // ---------------------------------------------------------------------------
        // IMAGE

        // We will support both native image tags AND scalar-image directives
        // For native image tags we need to map properties and provide the render method
        // if (node.tagName === 'img') {
        //   processAttributes(
        //     node,
        //     '',
        //     {
        //       src: node.properties?.src,
        //       alt: node.properties?.alt,
        //       size: 'actual',
        //       caption: node.properties?.title || '',
        //     },
        //     markdownImageDirective,
        //   )
        // }
      })
    }
  }

/**
 * Go through the list of nodes and populate the headings record
 *
 * We have already h-tags and markdown heading directives into custom headings
 *
 * This is run as a separate step for clarity and because heading may be created
 * either as markdown directives or as html h-tags.
 */
export const extractHeadings =
  (headings: Record<string, HeadingRecord>): Plugin =>
  () => {
    return (tree) => {
      // Typecast because unified Node types are not playing nice
      const rootChildren = (tree as any).children as unknown as HastNode[]
      // Heading search to generate proper id tags for l2 and l3 headings
      let parentSlug = ''
      rootChildren.forEach((node) => {
        // We have already h-tags and markdown heading directives into custom headings
        if (node.type !== 'element' || node.tagName !== 'custom_heading') return

        const attrs = safeJSONParse(node.properties?.dataAttrs)
        const nodeLevel = Number.parseInt(attrs.level as string, 10)

        const uid = nanoid()

        if (nodeLevel === 1) {
          headings[uid] = {
            uid,
            level: nodeLevel, // Offset
            slug: typeof attrs.slug === 'string' ? attrs.slug : '',
            name: flatContent(node),
          }
          parentSlug = headings[uid].slug
        }

        if (nodeLevel === 2) {
          headings[uid] = {
            uid,
            level: nodeLevel, // Offset
            slug: typeof attrs.slug === 'string' ? attrs.slug : '',
            name: flatContent(node),
            parent: parentSlug,
          }
        }
      })
    }
  }

/** Helper utility to merge nested text content into a string for heading ids */
function flatContent(node: HastNode): string {
  if (node.type !== 'element' || !node.children) return ''
  return node.children
    .map((c) => (c.type === 'text' ? c.value : flatContent(c)))
    .join('')
}

/**
 * Validate the attributes of a node against the component definition
 * and then set the tag name and properties so that the custom
 * component can be substituted by the components plugin
 */
function processAttributes({
  node,
  initialAttrs,
  tagNameOverride,
}: {
  node: HastElementNode
  /** Initial attributes. Any data-name attributes will override the initial value */
  initialAttrs: Partial<Record<string, string>>
  /** If provided we will override the tag name of the node */
  tagNameOverride?: string
}) {
  // For HTML tags we need to convert data attrs to raw properties
  const attrs = deepClone(initialAttrs)

  Object.entries(node.properties ?? {}).forEach(([key, value]) => {
    // If dataAttrs we set during previous steps (like markdown parsing) we need to merge them
    if (key === 'dataAttrs' || key === 'data-attrs') {
      return Object.assign(attrs, safeJSONParse(value))
    }

    // Handle data-name attributes with both camel and kebab case
    if (key.startsWith('data-')) {
      attrs[key.slice(4).toLowerCase()] = value
    } else if (key.startsWith('data')) {
      attrs[key.slice(4).toLowerCase()] = value
    } else {
      attrs[key] = value
    }
  })

  if (tagNameOverride) node.tagName = tagNameOverride

  // Merge to an unified dataAttrs property that will be parsed by the components plugin
  node.properties = shallowMerge(node.properties, {
    dataAttrs: JSON.stringify(attrs),
  })
}
